import React, { useState, useEffect, useCallback, createContext, useContext } from "react";
import { useAuth } from "./AuthContext";
import { API_URL } from "../lib/utils";

const PlanContext = createContext(null);

export const usePlan = () => useContext(PlanContext);

export const PlanProvider = ({ children }) => {
    const { token, isAuthenticated, user } = useAuth();
    const [planData, setPlanData] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchPlan = useCallback(async () => {
        if (!token || !isAuthenticated || user?.role === "platform_owner" || user?.role === "client_external") {
            setLoading(false);
            return;
        }
        try {
            const res = await fetch(`${API_URL}/subscription/usage`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (res.ok) {
                setPlanData(await res.json());
            }
        } catch (e) {
            console.error("Failed to fetch plan data:", e);
        } finally {
            setLoading(false);
        }
    }, [token, isAuthenticated, user?.role]);

    useEffect(() => { fetchPlan(); }, [fetchPlan]);

    const refreshPlan = fetchPlan;

    const canCreate = (resource) => {
        if (!planData) return true;
        const limits = planData.limits || {};
        const usage = planData.usage || {};
        const limitMap = { users: "users", projects: "projects" };
        const key = limitMap[resource];
        if (!key) return true;
        const limit = limits[key];
        if (limit === -1 || limit === undefined) return true;
        return (usage[key] || 0) < limit;
    };

    const hasFeature = (feature) => {
        if (!planData) return true;
        return (planData.features || []).includes(feature);
    };

    const getUsage = (resource) => {
        if (!planData) return { current: 0, limit: -1 };
        const limits = planData.limits || {};
        const usage = planData.usage || {};
        const limitMap = { users: "users", projects: "projects", storage: "storage_gb" };
        const key = limitMap[resource];
        if (!key) return { current: 0, limit: -1 };
        return { current: usage[key] || 0, limit: limits[key] ?? -1 };
    };

    return (
        <PlanContext.Provider value={{ planData, loading, refreshPlan, canCreate, hasFeature, getUsage }}>
            {children}
        </PlanContext.Provider>
    );
};
